import {useEffect, useState, type FormEvent} from 'react'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Hint} from '@/components/ui/hint'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {FieldError} from '@/components/field-error'
import {AuthShell} from '@/components/auth-shell'
import {useAuth} from '@/hooks/platform/use-auth'
import {api} from '@/lib/api'
import {translateApiError} from '@/lib/errors'
import {limpiarCredencialDeLaBarra} from '@/lib/url-redaction'
import {validatePassword} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

/** Claim (invitación) y reset son el mismo canje de token de un solo uso; cambian endpoint y copy. */
function SetPasswordScreen({mode}: {mode: 'claim' | 'reset'}) {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const {completeAuth} = useAuth()
  const [params] = useSearchParams()
  // El token se sostiene en estado y sale de la barra de direcciones apenas se lee:
  // es canjeable por una sesión y no tiene por qué quedar en el historial.
  const [token] = useState(() => params.get('token') ?? '')
  useEffect(limpiarCredencialDeLaBarra, [])
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  function blurPassword() {
    const problem = validatePassword(password)
    setPasswordError(problem ? t(problem) : null)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!token) {
      setError(t('setPassword.missingToken'))
      return
    }
    const problem = validatePassword(password)
    setPasswordError(problem ? t(problem) : null)
    if (problem) return
    if (password !== confirm) {
      setError(t('setPassword.mismatch'))
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      const {data} = await api.post(`/auth/${mode}`, {token, password})
      await completeAuth(data.access_token)
      navigate('/', {replace: true})
    } catch (err) {
      setError(translateApiError(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthShell>
      <h1 className="font-heading text-3xl tracking-tight text-foreground">
        {t(mode === 'claim' ? 'setPassword.claimTitle' : 'setPassword.resetTitle')}
      </h1>

      <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-5" data-testid="set-password-form">
        <div className="space-y-2">
          <Label htmlFor="new-password">{t('setPassword.newPassword')}</Label>
          <Input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
              setPasswordError(null)
              setError(null)
            }}
            onBlur={blurPassword}
            required
            maxLength={tope('PASS')}
            aria-invalid={!!passwordError}
          />
          <FieldError error={passwordError ?? undefined} />
          <Hint>{t('common.passwordHint')}</Hint>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-password">{t('setPassword.confirmPassword')}</Label>
          <Input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value)
              setError(null)
            }}
            required
            maxLength={tope('PASS')}
          />
        </div>
        {error && <p className="notice-red rounded-lg px-3.5 py-3 text-sm font-medium">{error}</p>}
        <Button type="submit" className="w-full" disabled={submitting}>
          {t('setPassword.submit')}
        </Button>
        <Link to="/login" className="block text-center text-sm text-primary hover:underline">
          {t('setPassword.backToLogin')}
        </Link>
      </form>
    </AuthShell>
  )
}

export function ClaimPage() {
  return <SetPasswordScreen mode="claim" />
}

export function ResetPage() {
  return <SetPasswordScreen mode="reset" />
}
